/**
 * Colour arithmetic for the token layer — WCAG contrast, and sRGB ↔ OKLCH.
 *
 * WHY THIS EXISTS AT RUNTIME. Until the brand axis there was no colour maths
 * in the product at all: every value in `styles.css` is hand-chosen and
 * `test/tokens.test.ts` measured it once, in a private copy of the contrast
 * formula, at test time. A school's colour arrives from a database, so the
 * derivation that keeps the contrast bars true has to run in the browser —
 * and the moment the same formula exists twice, the guard and the product can
 * disagree about what "4.5:1" means. So the test now imports from HERE. One
 * definition, measured by the suite that enforces the spec.
 *
 * Kept dependency-free and browser-safe: no colour library, no `color-mix()`
 * (which only exists inside a stylesheet), nothing that needs a layout engine.
 *
 * Two colour spaces, for two different jobs:
 *
 *  · **WCAG relative luminance** answers "can this be read on that?". It is a
 *    photometric measure and it is what the accessibility bars are written in.
 *    It is useless for *choosing* a colour, because it says nothing about hue.
 *  · **OKLCH** is a perceptual space (Björn Ottosson's Oklab, expressed as
 *    lightness / chroma / hue). Moving a colour along its L axis is what "the
 *    same colour, lighter" means to a human — a school's navy stays navy.
 *    Doing the same thing in sRGB or HSL visibly shifts the hue.
 *
 * The bridge between them is a search, not a formula (`brand.ts`): step L in
 * OKLCH, measure the result in WCAG.
 */

/** sRGB, gamma-encoded, 0–1 per channel — the space a hex literal is in. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** OKLCH: `l` 0–1, `c` 0–~0.4, `h` in degrees 0–360 (undefined-ish at c≈0). */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const HEX_PATTERN = /^#?([0-9a-f]{6})$/i;

/** Is this a 6-digit hex colour? The only form the brand columns accept. */
export function isHexColor(value: string): boolean {
  return HEX_PATTERN.test(value.trim());
}

/** Lowercased `#rrggbb`. Throws on anything else — callers guard with `isHexColor`. */
export function normalizeHex(hex: string): string {
  const match = HEX_PATTERN.exec(hex.trim());
  if (!match?.[1]) throw new Error(`not a 6-digit hex: ${hex}`);
  return `#${match[1].toLowerCase()}`;
}

export function parseHex(hex: string): Rgb {
  const h = normalizeHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function pair(value: number): string {
  return Math.round(clamp01(value) * 255)
    .toString(16)
    .padStart(2, '0');
}

/** `#rrggbb`, clamped and quantized — the only thing that can reach the DOM. */
export function formatHex({ r, g, b }: Rgb): string {
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

// ── WCAG 2.x ──────────────────────────────────────────────────────────

/**
 * WCAG's linearization threshold is 0.03928 as published (sRGB's own is
 * 0.04045 — the two differ by less than a 255th of a channel). The published
 * number is used here because these ratios are quoted in the design spec's
 * contrast bars, and a bar has to be reproducible against the standard that
 * states it. `linearize` below, which feeds the perceptual space rather than
 * the accessibility bar, uses the sRGB value.
 */
function wcagChannel(value: number): number {
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * wcagChannel(r) + 0.7152 * wcagChannel(g) + 0.0722 * wcagChannel(b);
}

/** WCAG contrast ratio, 1–21. Symmetric: order of arguments does not matter. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ];
  return (hi + 0.05) / (lo + 0.05);
}

// ── OKLab / OKLCH ─────────────────────────────────────────────────────
//
// Matrices and the cube-root nonlinearity are Ottosson's published Oklab
// (2020). They are transcribed, not derived; the round-trip test in
// `brand.test.ts` is what proves the transcription.

function linearize(value: number): number {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function encodeGamma(value: number): number {
  return value <= 0.0031308 ? value * 12.92 : 1.055 * value ** (1 / 2.4) - 0.055;
}

function rgbToOklab({ r, g, b }: Rgb): { l: number; a: number; b: number } {
  const lr = linearize(r);
  const lg = linearize(g);
  const lb = linearize(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return {
    l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

/** Oklab → LINEAR sRGB. May land outside 0–1: that is what "out of gamut" is. */
function oklabToLinearRgb({ l: L, a: A, b: B }: { l: number; a: number; b: number }): Rgb {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;

  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

export function hexToOklch(hex: string): Oklch {
  const { l, a, b } = rgbToOklab(parseHex(hex));
  const c = Math.hypot(a, b);
  // atan2 returns (-180, 180]; the hue circle is conventionally [0, 360).
  const h = c === 0 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

/** Is this OKLCH triple representable in sRGB, within a rounding tolerance? */
function inGamut({ l, c, h }: Oklch): boolean {
  const rad = (h * Math.PI) / 180;
  const rgb = oklabToLinearRgb({ l, a: c * Math.cos(rad), b: c * Math.sin(rad) });
  const eps = 1e-4;
  return (
    rgb.r >= -eps &&
    rgb.r <= 1 + eps &&
    rgb.g >= -eps &&
    rgb.g <= 1 + eps &&
    rgb.b >= -eps &&
    rgb.b <= 1 + eps
  );
}

/**
 * OKLCH → `#rrggbb`, GAMUT-MAPPED BY CHROMA.
 *
 * Most of the OKLCH cylinder is not a colour any screen can show, and the
 * naive fix — clamping each channel into 0–1 — changes hue and lightness
 * together, so "the same navy, lighter" comes back a different navy. Reducing
 * chroma instead holds hue and lightness exactly and only drains saturation,
 * which is the one axis a viewer is least likely to notice and the one the
 * brand derivation is willing to spend. That is the whole reason the accent
 * derivation can promise "hue held".
 *
 * The search is a fixed 20-step bisection on chroma so the result is
 * deterministic: the same input always produces the same hex, in the browser
 * and in the test.
 */
export function oklchToHex(color: Oklch): string {
  let { c } = color;
  if (!inGamut(color)) {
    let lo = 0; // c = 0 is a grey of this lightness — always representable
    let hi = c;
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (inGamut({ ...color, c: mid })) lo = mid;
      else hi = mid;
    }
    c = lo;
  }
  const rad = (color.h * Math.PI) / 180;
  const linear = oklabToLinearRgb({ l: color.l, a: c * Math.cos(rad), b: c * Math.sin(rad) });
  return formatHex({
    r: encodeGamma(clamp01(linear.r)),
    g: encodeGamma(clamp01(linear.g)),
    b: encodeGamma(clamp01(linear.b)),
  });
}
